/**
 * Tech Talk Sidebar Navigation
 * Generates navigation from article headings and handles smooth scrolling
 */

const TechTalkNav = (() => {
  // Configuration
  const CONFIG = {
    contentSelector: '#tech-talk-article-content',
    navListSelector: '.tech-talk-nav__list',
    headingSelectors: 'h2, h3, h4',
    activeClass: 'is-active',
    scrollOffset: 130,
    throttleDelay: 100
  };

  // State
  let headings = [];
  let navLinks = [];
  let isScrolling = false;
  let scrollTimeout = null;

  /**
   * Initialize the navigation
   */
  const init = () => {
    const contentContainer = document.querySelector(CONFIG.contentSelector);
    const navList = document.querySelector(CONFIG.navListSelector);

    if (!contentContainer || !navList) return;

    // Get all headings from content
    headings = Array.from(contentContainer.querySelectorAll(CONFIG.headingSelectors));

    if (headings.length === 0) return;

    // Generate IDs for headings and create navigation
    generateNavigation(headings, navList);

    // Cache nav links
    navLinks = Array.from(navList.querySelectorAll('.tech-talk-nav__link'));

    // Set up scroll listener
    setupScrollListener();

    // Set initial active state
    updateActiveState();
  };

  /**
   * Generate navigation items from headings
   */
  const generateNavigation = (headings, navList) => {
    const fragment = document.createDocumentFragment();

    headings.forEach((heading, index) => {
      // Generate unique ID if not present
      if (!heading.id) {
        heading.id = generateSlug(heading.textContent, index);
      }

      // Create nav item
      const li = document.createElement('li');
      li.className = 'tech-talk-nav__item';

      // Create nav link
      const link = document.createElement('a');
      link.className = 'tech-talk-nav__link';
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      link.setAttribute('data-target', heading.id);
      link.setAttribute('tabindex', '0');
      link.setAttribute('role', 'link');
      link.setAttribute('aria-label', `Navigate to ${heading.textContent.trim()}`);

      // Add click handler
      link.addEventListener('click', handleNavClick);

      // Add keyboard handler
      link.addEventListener('keydown', handleKeyDown);

      li.appendChild(link);
      fragment.appendChild(li);
    });

    navList.appendChild(fragment);
  };

  /**
   * Generate a URL-safe slug from text
   */
  const generateSlug = (text, index) => {
    const slug = text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);

    return `section-${slug}-${index}`;
  };

  /**
   * Handle navigation link click
   */
  const handleNavClick = (event) => {
    event.preventDefault();

    const targetId = event.currentTarget.getAttribute('data-target');
    const targetElement = document.getElementById(targetId);

    if (!targetElement) return;

    // Set scrolling flag
    isScrolling = true;

    // Calculate scroll position
    const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - CONFIG.scrollOffset;

    // Smooth scroll to target
    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });

    // Update active state immediately
    setActiveLink(event.currentTarget);

    // Update URL hash without jumping
    history.pushState(null, null, `#${targetId}`);

    // Reset scrolling flag after animation
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      isScrolling = false;
    }, 1000);
  };

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavClick(event);
    }
  };

  /**
   * Set up scroll listener with throttling
   */
  const setupScrollListener = () => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking && !isScrolling) {
        window.requestAnimationFrame(() => {
          updateActiveState();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
  };

  /**
   * Update active state based on scroll position
   */
  const updateActiveState = () => {
    const scrollPosition = window.pageYOffset + CONFIG.scrollOffset + 50;

    let activeHeading = null;

    // Find the current section
    for (let i = headings.length - 1; i >= 0; i--) {
      const heading = headings[i];
      const headingTop = heading.getBoundingClientRect().top + window.pageYOffset;

      if (scrollPosition >= headingTop) {
        activeHeading = heading;
        break;
      }
    }

    // Update nav link active states
    let newActiveLink = null;
    navLinks.forEach(link => {
      const targetId = link.getAttribute('data-target');

      if (activeHeading && targetId === activeHeading.id) {
        link.classList.add(CONFIG.activeClass);
        link.setAttribute('aria-current', 'true');
        newActiveLink = link;
      } else {
        link.classList.remove(CONFIG.activeClass);
        link.removeAttribute('aria-current');
      }
    });

    // Scroll active link into view within sidebar
    if (newActiveLink) {
      scrollActiveLinkIntoView(newActiveLink);
    }
  };

  /**
   * Set active link manually
   */
  const setActiveLink = (activeLink) => {
    navLinks.forEach(link => {
      link.classList.remove(CONFIG.activeClass);
      link.removeAttribute('aria-current');
    });

    activeLink.classList.add(CONFIG.activeClass);
    activeLink.setAttribute('aria-current', 'true');

    // Scroll active link into view within sidebar
    scrollActiveLinkIntoView(activeLink);
  };

  /**
   * Scroll active link into view within sidebar container
   */
  const scrollActiveLinkIntoView = (activeLink) => {
    const sidebar = document.querySelector('.tech-talk-sidebar');
    if (!sidebar || !activeLink) return;

    const sidebarRect = sidebar.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();

    // Check if link is outside visible area of sidebar
    const isAbove = linkRect.top < sidebarRect.top;
    const isBelow = linkRect.bottom > sidebarRect.bottom;

    if (isAbove || isBelow) {
      activeLink.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  /**
   * Handle initial hash in URL
   */
  const handleInitialHash = () => {
    if (window.location.hash) {
      const targetId = window.location.hash.substring(1);
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        setTimeout(() => {
          const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - CONFIG.scrollOffset;
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      handleInitialHash();
    });
  } else {
    init();
    handleInitialHash();
  }

  // Return public methods for external access if needed
  return {
    init,
    updateActiveState
  };
})();


